import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type RefObject,
} from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowSimple, Copy, t } from '@stokr/components-library'
import type { CopyKey } from 'locales/copy-keys.gen'
import { StoPageActionsProvider, useStoPageActions } from 'context/StoPageActionsContext'
import type { ModalType } from 'shared/domain/projectEligibility'
import { useIsBelowDesktop } from 'hooks/sto/useIsBelowDesktop'
import { getAnchorIdFromHash } from 'hooks/sto/useStoAnchors'
import { useStoProject } from 'hooks/sto/useStoProject'
import { useSectionViewTracking, useStoTrack } from 'hooks/sto/useStoTracking'
import { ProjectTypes } from 'constants/enums'
import { stoQueries } from 'hooks/sto/queries/stoQueries'
import { pickTradeVenue, useTradePanel, type TradeVenueSource } from 'hooks/sto/useTrading'
import { pickContent, StoContentTypes, type GeneralContent } from 'models/projectContent'
import { InvestPanel } from './InvestPanel'
import { TradePanel } from './TradePanel'
import { RedeemPanel } from './RedeemPanel'
import { MARKET_TYPES } from 'shared/domain/projectEligibility'
import { toRedemptionCompleted } from 'hooks/sto/redemptionCompleted'
import { EligibilityCheckFooter } from './EligibilityCheckFooter'
import { RedemptionCompleted } from './RedemptionCompleted'
import { CardTabs, TabPanel } from 'components/sto/Tabs'
import {
  Backdrop,
  Card,
  MobileBar,
  MobileBarPageSpace,
  PanelBody,
  PanelViewport,
  PrimaryAction,
  Root,
  SheetBody,
  SheetClose,
  SheetHeader,
} from './KeyActions.styles'

type KeyActionsTab = 'invest' | 'trade' | 'redeem'

const TABS: { id: KeyActionsTab; labelKey: CopyKey }[] = [
  { id: 'invest', labelKey: 'stoPage.keyActions.tabs.invest' },
  { id: 'trade', labelKey: 'stoPage.keyActions.tabs.trade' },
  { id: 'redeem', labelKey: 'stoPage.keyActions.tabs.redeem' },
]

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'

/**
 * The mobile sheet. While open: focus moves into it and stays there (Tab wraps), Escape closes
 * it, the page behind does not scroll; on close, focus goes back to the bar button. Keys pressed
 * while focus is elsewhere (a library modal) are left alone. Growing to desktop closes it, and
 * a mobile `#key-actions` link opens it (on desktop that anchor just scrolls to the card).
 */
function useMobileSheet(
  open: boolean,
  setOpen: (open: boolean) => void,
  sheetRef: RefObject<HTMLDivElement | null>,
  barRef: RefObject<HTMLDivElement | null>,
) {
  const isMobile = useIsBelowDesktop()
  const { hash } = useLocation()

  useEffect(() => {
    if (!isMobile) setOpen(false)
    else if (getAnchorIdFromHash(hash) === 'key-actions') setOpen(true)
  }, [isMobile, hash, setOpen])

  useEffect(() => {
    const sheet = sheetRef.current
    if (!open || !sheet) return undefined
    const ownsFocus = () => sheet.contains(document.activeElement) || document.activeElement === document.body

    const onKey = (event: KeyboardEvent) => {
      if (!ownsFocus()) return
      if (event.key === 'Escape') {
        setOpen(false)
        return
      }
      if (event.key !== 'Tab') return
      const focusable = [...sheet.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.getClientRects().length > 0,
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return
      const active = document.activeElement
      if (event.shiftKey ? active === first || !sheet.contains(active) : active === last) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
      }
    }

    // iOS Safari ignores `overflow: hidden` for touch scrolling, so swipes are guarded too: outside
    // the sheet they never scroll the page, inside it they stop at its edges instead of chaining
    // to the page. Overlays portalled outside #root (library modals) keep their own scrolling.
    const appRoot = document.getElementById('root')
    let start = { x: 0, y: 0 }
    const onTouchStart = (event: TouchEvent) => {
      start = { x: event.touches[0].clientX, y: event.touches[0].clientY }
    }
    const onTouchMove = (event: TouchEvent) => {
      const target = event.target as Node | null
      if (!target || !appRoot?.contains(target)) return
      if (!sheet.contains(target)) {
        event.preventDefault()
        return
      }
      const dx = event.touches[0].clientX - start.x
      const dy = event.touches[0].clientY - start.y
      if (Math.abs(dx) > Math.abs(dy)) return
      // Scrolling the sheet closes the keyboard, as in native iOS apps.
      const field = document.activeElement
      if (field instanceof HTMLInputElement && sheet.contains(field) && target !== field && Math.abs(dy) > 10)
        field.blur()
      const atTop = sheet.scrollTop <= 0
      const atBottom = sheet.scrollTop + sheet.clientHeight >= sheet.scrollHeight - 1
      if ((atTop && dy > 0) || (atBottom && dy < 0)) event.preventDefault()
    }

    // On <html>, not <body>: the library Modal (opened from inside the sheet) resets body styles on close.
    const root = document.documentElement
    const previousOverflow = root.style.overflow
    root.style.overflow = 'hidden'
    // iOS scrolls the locked page anyway to make room for the keyboard; put it back on close.
    const pageScroll = window.scrollY
    sheet.focus({ preventScroll: true })
    window.addEventListener('keydown', onKey)
    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchmove', onTouchMove, { passive: false })
    return () => {
      root.style.overflow = previousOverflow
      if (window.scrollY !== pageScroll) window.scrollTo(0, pageScroll)
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('touchstart', onTouchStart)
      document.removeEventListener('touchmove', onTouchMove)
      // On desktop the bar is hidden and focus() is a no-op, so focus stays in the inline card.
      if (sheet.contains(document.activeElement))
        barRef.current?.querySelector('button')?.focus({ preventScroll: true })
    }
  }, [open, setOpen, sheetRef, barRef])
}

/** About how long iOS takes to slide the keyboard away. */
const KEYBOARD_CLOSE_MS = 350
const SHRINK: KeyframeAnimationOptions = { duration: 300, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }

/**
 * Typing on a phone takes the sheet full screen at once (`expanded`). When the field loses focus
 * the sheet waits for the keyboard to finish closing, so nothing moves under a tap meant for a
 * tab, then shrinks smoothly to its own height. Closing the sheet keeps it as it is for the slide
 * out; it starts at its own height the next time it opens.
 */
function useSheetExpansion(sheetRef: RefObject<HTMLDivElement | null>) {
  const [expanded, setExpanded] = useState(false)
  const releaseTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const shrinkFrom = useRef<number | null>(null)

  useLayoutEffect(() => {
    const sheet = sheetRef.current
    const from = shrinkFrom.current
    shrinkFrom.current = null
    if (expanded || from === null || !sheet || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const to = sheet.offsetHeight
    if (to < from)
      sheet.animate(
        [
          { height: `${from}px`, maxHeight: `${from}px` },
          { height: `${to}px`, maxHeight: `${to}px` },
        ],
        SHRINK,
      )
  }, [expanded, sheetRef])

  useEffect(() => () => clearTimeout(releaseTimer.current), [])

  const onFocus = useCallback((event: FocusEvent<HTMLDivElement>) => {
    if (!(event.target instanceof HTMLInputElement) || !window.matchMedia('(pointer: coarse)').matches) return
    clearTimeout(releaseTimer.current)
    setExpanded(true)
  }, [])

  const onBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      if (!(event.target instanceof HTMLInputElement)) return
      clearTimeout(releaseTimer.current)
      releaseTimer.current = setTimeout(() => {
        shrinkFrom.current = sheetRef.current?.offsetHeight ?? null
        setExpanded(false)
      }, KEYBOARD_CLOSE_MS)
    },
    [sheetRef],
  )

  /** Sheet opened or closed: no shrink pending; a new open starts at the sheet's own height. */
  const onSheetToggle = useCallback((open: boolean) => {
    clearTimeout(releaseTimer.current)
    if (open) setExpanded(false)
  }, [])

  return { expanded, onFocus, onBlur, onSheetToggle }
}

/** Mirrors the panel's height into `--panel-height`, which only the mobile sheet animates. */
function usePanelHeight() {
  const viewportRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const viewport = viewportRef.current
    const content = contentRef.current
    if (!viewport || !content || typeof ResizeObserver === 'undefined') return undefined
    const update = () => viewport.style.setProperty('--panel-height', `${content.offsetHeight}px`)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(content)
    return () => observer.disconnect()
  }, [])
  return { viewportRef, contentRef }
}

/**
 * Invest/Trade/Redeem rail.
 *
 * All three tabs always show; each panel says what the product and the visitor allow. The
 * eligibility footer underneath checks the secondary market under Trade, the primary
 * otherwise. All three panels follow the product and the signed-in visitor.
 */
export const KeyActions = () => {
  const { project, projectId } = useStoProject()
  const { data: content } = useQuery(stoQueries.content(projectId))
  const [pickedTab, setActiveTab] = useState<KeyActionsTab>()
  // Equity trades on the secondary market, so it opens on Trade; the rest on Invest.
  const defaultTab: KeyActionsTab = project?.type === ProjectTypes.EQUITY ? 'trade' : 'invest'
  const activeTab = pickedTab ?? defaultTab
  // The Trade tab's venue, held here: the eligibility footer follows it.
  const [pickedVenue, setPickedVenue] = useState<TradeVenueSource>()
  const { venues } = useTradePanel()
  // Fully redeemed: one celebratory panel instead of the tabs.
  const completed = toRedemptionCompleted(project, pickContent<GeneralContent>(content, StoContentTypes.GENERAL))
  const isTrade = activeTab === 'trade' && !completed
  const panelId = useId()
  // Mobile only: the bottom sheet. On desktop it is never opened, and the card sits inline.
  const [sheetOpen, setSheetOpenState] = useState(false)
  const sheetRef = useRef<HTMLDivElement>(null)
  const expansion = useSheetExpansion(sheetRef)
  const { onSheetToggle } = expansion
  const setSheetOpen = useCallback(
    (open: boolean) => {
      setSheetOpenState(open)
      onSheetToggle(open)
    },
    [onSheetToggle],
  )
  const isMobile = useIsBelowDesktop()
  const barRef = useRef<HTMLDivElement>(null)
  useMobileSheet(sheetOpen, setSheetOpen, sheetRef, barRef)
  const { viewportRef, contentRef } = usePanelHeight()
  const trackSto = useStoTrack()
  // On mobile the closed sheet is off screen: it counts once opened.
  useSectionViewTracking(sheetRef, 'key-actions')
  const selectTab = (tab: KeyActionsTab) => {
    if (tab !== activeTab) trackSto('sto_key_actions_tab_selected', { tab })
    setActiveTab(tab)
  }

  // Page modals (log in, private investor) sit in the header's stacking layer, under the sheet:
  // the sheet closes first so the modal is never hidden behind it.
  const page = useStoPageActions()
  const openLoginModal = useCallback(() => {
    setSheetOpen(false)
    page.openLoginModal()
  }, [page, setSheetOpen])
  const openStepModal = useCallback(
    (modal: ModalType) => {
      setSheetOpen(false)
      page.openStepModal(modal)
    },
    [page, setSheetOpen],
  )

  return (
    <>
      <Backdrop $open={sheetOpen} onClick={() => setSheetOpen(false)} aria-hidden="true" />
      <Root
        ref={sheetRef}
        tabIndex={-1}
        id="key-actions"
        data-sto-section="key-actions"
        $open={sheetOpen}
        $expanded={expansion.expanded}
        onFocus={sheetOpen ? expansion.onFocus : undefined}
        onBlur={expansion.onBlur}
        role={sheetOpen ? 'dialog' : undefined}
        aria-modal={sheetOpen || undefined}
        aria-label={sheetOpen ? t('stoPage.keyActions.mobile.title') : undefined}>
        <SheetHeader>
          <Copy id="stoPage.keyActions.mobile.title" />
          <SheetClose
            type="button"
            aria-label={t('common.close')}
            data-cy="sto-key-actions-sheet-close"
            onClick={() => setSheetOpen(false)}>
            <ArrowSimple width={12} height={8} aria-hidden="true" />
          </SheetClose>
        </SheetHeader>

        <StoPageActionsProvider openLoginModal={openLoginModal} openStepModal={openStepModal}>
          <SheetBody>
            <Card>
              {/* Keyed by the default: when the project arrives after the first render (no SSR),
                  the tabs start over on it instead of sliding there. */}
              {!completed && (
                <CardTabs
                  key={defaultTab}
                  tabs={TABS.map((tab) => ({ id: tab.id, label: <Copy id={tab.labelKey} /> }))}
                  activeId={activeTab}
                  onSelect={selectTab}
                  panelId={panelId}
                  dataCy="sto-key-actions-tab"
                />
              )}

              <PanelViewport ref={viewportRef}>
                <div ref={contentRef}>
                  {completed ? (
                    // On mobile the card sits in the closed sheet: the show waits until it opens.
                    <RedemptionCompleted {...completed} celebrate={!isMobile || sheetOpen} />
                  ) : (
                    <TabPanel key={defaultTab} activeKey={activeTab} id={panelId}>
                      <PanelBody>
                        {activeTab === 'invest' && <InvestPanel />}
                        {activeTab === 'trade' && (
                          <TradePanel pickedSource={pickedVenue} onPickSource={setPickedVenue} />
                        )}
                        {activeTab === 'redeem' && <RedeemPanel />}
                      </PanelBody>
                    </TabPanel>
                  )}
                </div>
              </PanelViewport>
            </Card>

            {/* A completed product has no Trade tab: its signed-out prompt is the general one. A venue
                that onboards on its own (Bitfinex) needs no STOKR steps, so none are listed for it. */}
            {!(isTrade && pickTradeVenue(venues, pickedVenue)?.needsStokrEligibility === false) && (
              <EligibilityCheckFooter market={isTrade ? MARKET_TYPES.SECONDARY : MARKET_TYPES.PRIMARY} />
            )}
          </SheetBody>
        </StoPageActionsProvider>
      </Root>

      <MobileBarPageSpace />
      <MobileBar ref={barRef}>
        <PrimaryAction
          type="button"
          aria-expanded={sheetOpen}
          aria-controls="key-actions"
          data-cy="sto-key-actions-mobile-open"
          onClick={() => {
            trackSto('sto_key_actions_sheet_opened', { tab: activeTab })
            setSheetOpen(true)
          }}>
          <Copy id="stoPage.keyActions.mobile.title" />
        </PrimaryAction>
      </MobileBar>
    </>
  )
}
